import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';

import { AbstractMethodComponent } from '../abstract-method.component';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { CREDIT_CARD_DEFAULT_STYLES, CreditCardStyles } from '../../payment-methods.model';
import { OrderFormValidationResult } from '@orderform/widgets';
import { ExternalResourcesLoaderService } from '../../../../services/external-resources-loader.service';
import { HttpClient } from '@angular/common/http';
import { CustomErrorMessageService } from '../../services/custom-error-messages.service';

declare let braintree: any;

@Component({
  selector: 'orderform-payment-method-braintree',
  templateUrl: './braintree.component.html',
  styleUrls: ['./braintree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BraintreeComponent),
      multi: true,
    },
  ],
})
export class BraintreeComponent
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit
{
  @Input() config: {
    payment_provider_id: number;
    jsSdkUrls: string[];
    sdkType: 'hostedFields' | 'dropin';
    locale: string;
    has3dSecure: boolean;
    debugLabel: string;
    method: 'creditcard';
    config: any;
    clientIp: string;
  };
  @Input() styles?: CreditCardStyles = CREDIT_CARD_DEFAULT_STYLES;
  private _subscriptionBeforeSubmit = this._orderformFascade.beforeSubmit.subscribe(() => this._onBeforeSubmit());
  private _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onFormValidation(),
    this._element
  );

  private _braintreeDropinInstance: any;

  private _waitForLoad: Promise<void>;

  public creditCardType$ = new Subject<string>();
  public successByField$ = {
    number: new BehaviorSubject<boolean>(false),
    expirationDate: new BehaviorSubject<boolean>(false),
    cvv: new BehaviorSubject<boolean>(false),
  };
  public errorByField$ = {
    number: new BehaviorSubject<string>(''),
    expirationDate: new BehaviorSubject<string>(''),
    cvv: new BehaviorSubject<string>(''),
  };
  public touchedByField$ = {
    number: new BehaviorSubject<boolean>(false),
    expirationDate: new BehaviorSubject<boolean>(false),
    cvv: new BehaviorSubject<boolean>(false),
  };
  private _braintreeHostedFieldsInstance: any;
  private _braintreeThreeDSecureInstance: any;

  private clientToken: string;

  constructor(
    private _orderformFascade: OrderformFascade,
    private _ngZone: NgZone,
    injector: Injector,
    public externalResourcesLoader: ExternalResourcesLoaderService,
    private customErrorMessageService: CustomErrorMessageService,
    private readonly http: HttpClient
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this._waitForLoad = this._load();
    this._debugLabel = this.config.debugLabel;
  }

  private async _load(): Promise<void> {
    // Check if braintree is already defined before loading the SDK
    if (typeof braintree === 'undefined') {
      await this.externalResourcesLoader.load(this._document, this.config.jsSdkUrls);
    }
  }

  public async ngAfterViewInit() {
    await this._waitForLoad;

    if (this.config.sdkType === 'hostedFields') {
      await this._createHostedFieldsInstance();
    } else {
      this._createDropinInstance();
    }
  }

  private async getClientToken(): Promise<string> {
    if (!this.clientToken) {
      const { protocol, hostname, port } = window.location;
      let baseUrl = `${protocol}//${hostname}`;
      if (port) {
        baseUrl += ':' + port;
      }
      const apiUrl = baseUrl + '/buy/braintree/client/token';
      const postData = {
        payment_provider_id: this.config.payment_provider_id,
      };

      this.clientToken = await firstValueFrom(this.http.post<string>(apiUrl, postData));
    }

    return this.clientToken;
  }

  private async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }

    await this._waitForLoad;

    if (this.config.sdkType === 'hostedFields') {
      return await this._onBeforeSubmitHostedFields();
    } else {
      return await this._onBeforeSubmitDropin();
    }
  }
  private async _onBeforeSubmitDropin(): Promise<{ valid: boolean }> {
    const requestPaymentConfig: { threeDSecure?: any } = {};
    const orderData = await firstValueFrom(this._orderformFascade.order$);

    if (this.config.has3dSecure) {
      const helperGetDataForAddressType = (isBilling: boolean) => {
        const clone = {
          givenName: 'buyer_address_first_name',
          surname: 'buyer_address_last_name',
          phoneNumber: 'buyer_address_phone_no',
          streetAddress: 'buyer_address_street',
          extendedAddress: 'buyer_address_street_number',
          locality: 'buyer_address_city',
          region: 'buyer_address_state',
          postalCode: 'buyer_address_zipcode',
          countryCodeAlpha2: 'buyer_address_country',
        };
        Object.keys(clone).forEach((key) => {
          if (isBilling) {
            clone[key] = orderData.order.billing_address[clone[key] + '_billing'];
          } else {
            clone[key] = orderData.order.shipping_address[clone[key]];
          }
        });
        return clone;
      };

      const shippingAddress = helperGetDataForAddressType(false);
      // Check is billing address active
      // if not, set billingAddress data to empty object because default would be the shipping address
      const billingAddress = orderData.order.has_billing_address ? helperGetDataForAddressType(true) : ({} as any);

      requestPaymentConfig.threeDSecure = {
        amount: Math.max(1.0, orderData.order_summary.order_final.gross_1st),
        email: orderData.order.shipping_address.buyer_address_email,
        billingAddress: {
          givenName: billingAddress.givenName || shippingAddress.givenName || '',
          surname: billingAddress.surname || shippingAddress.surname || '',
          phoneNumber: billingAddress.phoneNumber || shippingAddress.phoneNumber || '',
          streetAddress: billingAddress.streetAddress || shippingAddress.streetAddress,
          extendedAddress: billingAddress.extendedAddress || shippingAddress.extendedAddress || '',
          locality: billingAddress.locality || shippingAddress.locality || '',
          region: billingAddress.region || shippingAddress.region || '',
          postalCode: billingAddress.postalCode || shippingAddress.postalCode || '',
          countryCodeAlpha2: billingAddress.countryCodeAlpha2 || shippingAddress.countryCodeAlpha2 || '',
        },
        additionalInformation: {
          workPhoneNumber: shippingAddress.phoneNumber || '',
          shippingGivenName: shippingAddress.givenName || '',
          shippingSurname: shippingAddress.surname || '',
          shippingPhone: shippingAddress.phoneNumber || '',
          shippingAddress: {
            streetAddress: shippingAddress.streetAddress || '',
            extendedAddress: shippingAddress.extendedAddress || '',
            locality: shippingAddress.locality || '',
            region: shippingAddress.region || '',
            postalCode: shippingAddress.postalCode || '',
            countryCodeAlpha2: shippingAddress.countryCodeAlpha2 || '',
          },
          ipAddress: this.config.clientIp,
        },
        collectDeviceData: true,
      };
    }

    return this._braintreeDropinInstance
      .requestPaymentMethod(requestPaymentConfig)
      .then((payload) => {
        if (payload && typeof payload.nonce != 'undefined') {
          const device_data = typeof payload.deviceData === 'string' ? payload.deviceData.replace(/\"/g, '::Q::') : '';

          this._paymentDataChanged.next({
            valid: true,
            braintree_nonce: payload.nonce,
            braintree_device: device_data,
          });
          return { valid: true };
        } else {
          this._paymentDataChanged.next({
            valid: false,
          });
          return { valid: false };
        }
      })
      .catch(async (error) => {
        console.log('Braintree Dropin error:', error);

        const orderData = await firstValueFrom(this._orderformFascade.order$);
        let errorCode = this._extractErrorCodeFromBraintreeError(error);

        if (error && error.code) {
          errorCode = error.code;
        } else if (error && error.message && error.message.includes('BTEC-')) {
          const match = error.message.match(/BTEC-(\d+)/);
          if (match && match[1]) {
            errorCode = match[1];
          }
        }

        const errorResult = {
          error: {
            code: errorCode,
            message: error.message || 'Payment failed',
          },
        };

        const declinedErrorMessage = this.customErrorMessageService.getCustomErrorMessageFromOrderData(
          errorResult,
          orderData
        );

        const errorElement = document.querySelector('.braintree-sheet__error-text');
        const isErrorElementVisible = errorElement && errorElement.getBoundingClientRect().height > 0;
        if (!isErrorElementVisible) {
          this._toaster.error(declinedErrorMessage);
        }

        const labels = this._monitoring.getLabelsFromFailedTransaction(
          orderData,
          error,
          'ds-orderform-payment-braintree'
        );
        this._monitoring.addLabels(labels);

        this._monitoring.trackValidationError(
          'exception:' + error.message,
          'frontend',
          'ds-orderform-payment-braintree'
        );
        this._paymentDataChanged.next({
          valid: false,
        });
        return { valid: false };
      });
  }
  private _extractErrorCodeFromBraintreeError(error: any): string | null {
    if (!error) return null;

    if (error.code) return error.code;

    if (error.message && typeof error.message === 'string') {
      const match = error.message.match(/BTEC-([a-zA-Z0-9_]+)/);
      if (match && match[1]) return match[1];
    }

    if (error.response && error.response.data) {
      if (error.response.data.code) return error.response.data.code;
      if (error.response.data.error_code) return error.response.data.error_code;
    }

    if (error.details && error.details.originalError) {
      return error.details.originalError.code;
    }

    return null;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._subscriptionBeforeSubmit.unsubscribe();
    this._subscriptionFormValidation.unsubscribe();
  }

  private async _onBeforeSubmitHostedFields(): Promise<{ valid: boolean }> {
    const orderData = await firstValueFrom(this._orderformFascade.order$.pipe());
    try {
      const payload = await this._braintreeHostedFieldsInstance.tokenize();
      let nonce = payload.nonce;
      let deviceData = payload.deviceData;
      if (this.config.has3dSecure) {
        const hasBillingAddress = orderData.order.has_billing_address;
        const addressData = hasBillingAddress ? orderData.order.billing_address : orderData.order.shipping_address;

        const threeDSecurePayload = await this._braintreeThreeDSecureInstance.verifyCard({
          onLookupComplete: (data: any, next: () => void) => {
            next();
          },
          nonce: payload.nonce,
          bin: payload.details.bin,
          amount: Math.max(1.0, orderData.order_summary.order_final.gross_1st),
          email: orderData.order.shipping_address.buyer_address_email,
          billingAddress: {
            givenName: addressData.buyer_address_first_name || '',
            surname: addressData.buyer_address_last_name || '',
            phoneNumber: orderData.order.shipping_address.buyer_address_phone_no || '',
            streetAddress:
              (addressData.buyer_address_street || '') + ' ' + (addressData.buyer_address_street_number || ''),
            locality: addressData.buyer_address_city || '',
            region: addressData.buyer_address_state || '',
            postalCode: addressData.buyer_address_zipcode || '',
            countryCodeAlpha2: addressData.buyer_address_country || '',
          },
          additionalInformation: {
            ipAddress: this.config.clientIp,
          },
          collectDeviceData: true,
        });

        if (!threeDSecurePayload.liabilityShifted) {
          const liabilityError = new Error('Liability did not shift');
          throw liabilityError;
        }

        nonce = threeDSecurePayload.nonce;
        deviceData = threeDSecurePayload.deviceData;
      }

      const device_data = typeof deviceData != 'undefined' ? deviceData.replace(/\"/g, '::Q::') : '';

      this._paymentDataChanged.next({
        valid: true,
        braintree_nonce: nonce,
        braintree_device: device_data,
      });
      return { valid: true };
    } catch (error) {
      if (error?.details?.invalidFieldKeys?.length > 0) {
        error?.details?.invalidFieldKeys.forEach((fieldKey) => {
          this.successByField$[fieldKey].next(false);
          this.errorByField$[fieldKey].next($localize`:@@payment_method.error.invalid_input:Ungültige Eingabe.`);
          this._monitoring.trackValidationError(fieldKey + ':invalid', 'frontend', 'ds-orderform-payment-braintree');

          const labels = this._monitoring.getLabelsFromFailedTransaction(
            orderData,
            error,
            'ds-orderform-payment-braintree'
          );
          this._monitoring.addLabels(labels);
        });
      } else {
        console.log('Braintree error Hosted fields :', error);
        let errorCode = this._extractErrorCodeFromBraintreeError(error);

        const errorResult = {
          error: {
            code: errorCode,
            message: error.message || 'Payment failed',
          },
        };

        const declinedErrorMessage = this.customErrorMessageService.getCustomErrorMessageFromOrderData(
          errorResult,
          orderData
        );

        this._toaster.error(declinedErrorMessage);
        this._monitoring.trackValidationError(
          'exception:' + error.message,
          'frontend',
          'ds-orderform-payment-braintree'
        );

        const labels = this._monitoring.getLabelsFromFailedTransaction(
          orderData,
          error,
          'ds-orderform-payment-braintree'
        );
        this._monitoring.addLabels(labels);
      }

      this._paymentDataChanged.next({
        valid: false,
      });
      return { valid: false };
    }
  }

  private async _createDropinInstance() {
    braintree.dropin.create(
      {
        ...this.config.config,
        authorization: await this.getClientToken(),
        container: '#braintree-container-' + this.uniqueInstanceId,
        locale: this.config.locale,
        fields: {
          number: {
            container: '#braintree-card-number-' + this.uniqueInstanceId,
            placeholder: '4444 3333 2222 1111',
          },
          cvv: {
            container: '#braintree-cvv-' + this.uniqueInstanceId,
            placeholder: '•••',
          },
          expirationDate: {
            container: '#braintree-expiration-date-' + this.uniqueInstanceId,
            placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
          },
        },
      },
      (error, dropinInstance) => {
        if (!dropinInstance) {
          this._hidePaymentMethodWithError(error, 'braintree');
          return;
        }

        this._braintreeDropinInstance = dropinInstance;
      }
    );
  }

  private async _createHostedFieldsInstance() {
    try {
      this._braintreeHostedFieldsInstance = await braintree.hostedFields.create({
        authorization: await this.getClientToken(),
        locale: this.config.locale,
        fields: {
          number: {
            container: '#braintree-card-number-' + this.uniqueInstanceId,
            placeholder: '4444 3333 2222 1111',
          },
          cvv: {
            container: '#braintree-cvv-' + this.uniqueInstanceId,
            placeholder: '●●●',
          },
          expirationDate: {
            container: '#braintree-expiration-date-' + this.uniqueInstanceId,
            placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
          },
        },
        styles: {
          'input,select': {
            'font-size': parseFloat(this.styles.fontSize) * 16 + 'px',
            color: this.styles.textColor,
          },

          'input:-webkit-autofill, input::placeholder': {
            color: this.styles.placeholderColor,
          },
        },
      });

      this._braintreeHostedFieldsInstance.on('cardTypeChange', (e) => {
        this._ngZone.run(() => {
          if (e.cards.length === 1) {
            this.creditCardType$.next(e.cards[0].type);
          } else {
            this.creditCardType$.next('');
          }
        });
      });

      this._braintreeHostedFieldsInstance.on('blur', (event) => {
        this._ngZone.run(() => {
          this.touchedByField$[event.emittedBy].next(true);
        });
      });

      this._braintreeHostedFieldsInstance.on('validityChange', (event) => {
        this._ngZone.run(() => {
          const field = event.fields[event.emittedBy];
          this.touchedByField$[event.emittedBy].next(true);
          if (this.successByField$[event.emittedBy]) {
            this.successByField$[event.emittedBy].next(field.isValid);
            if (!field.isValid) {
              this.errorByField$[event.emittedBy].next(
                $localize`:@@payment_method.error.invalid_input:Ungültige Eingabe.`
              );
            } else {
              this.errorByField$[event.emittedBy].next('');
            }
          }
        });
      });

      if (this.config.has3dSecure) {
        this._braintreeThreeDSecureInstance = await braintree.threeDSecure.create({
          authorization: await this.getClientToken(),
          locale: this.config.locale,
          version: 2,
        });
      }
    } catch (error) {
      this._hidePaymentMethodWithError(error, 'braintree');
    }
  }

  private _onFormValidation(): Promise<OrderFormValidationResult> {
    if (!this.isActive()) {
      return Promise.resolve({
        valid: true,
        component: 'ds-orderform-payment-braintree',
        invalidFieldList: [],
      });
    }

    const invalidFieldList = Object.keys(this.errorByField$)
      .filter((field) => this.errorByField$[field].value)
      .map((field) => ({
        id: field,
        errors: [this.errorByField$[field].value],
      }));
    return Promise.resolve({
      valid: invalidFieldList.length === 0,
      component: 'ds-orderform-payment-braintree',
      invalidFieldList,
    });
  }
}
